/**
 * ClientWorkspaceSidebar — vertical project list for the lead workspace.
 *
 * Replaces the old horizontal project-tab strip: every project is always
 * visible (no horizontal scrolling), each row shows number, descriptor, and a
 * live stage chip from the portal stage engine, so "which project am I on"
 * is never ambiguous. Also hosts the portal link/PIN block and workspace nav.
 */

import React, { useEffect, useRef, useState } from 'react';
import type { EnhancedDeal, PortalStage } from '../../types';
import { PORTAL_STAGE_SHORT_LABELS } from '../../utils/portalStageEngine';
import {
  ArrowRightLeft,
  Calculator,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  FileText,
  FolderKanban,
  Link2,
  Lock,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react';
import '../ops/OpsSidebar.css';
import './ClientWorkspaceSidebar.css';

export interface SidebarPortalInfo {
  slug: string;
  accessPin: string;
}

interface ClientWorkspaceSidebarProps {
  visibleDeals: EnhancedDeal[];
  archivedDeals: EnhancedDeal[];
  activeDealId: string | null;
  /** Stage chip for a project row — computed by the portal stage engine. */
  getDealStage: (deal: EnhancedDeal) => PortalStage | null;
  getDealDisplay: (deal: EnhancedDeal) => { number: string; descriptor: string };
  onSelectDeal: (dealId: string, opts: { archivedRow: boolean }) => void;
  onRenameDeal: (deal: EnhancedDeal) => void;
  onTransferDeal?: (deal: EnhancedDeal) => void;
  onToggleVisibility: (dealId: string) => void;
  onDeleteDeal: (dealId: string, name: string) => void;
  onAddProject: () => void;
  portal: SidebarPortalInfo | null;
  copied: boolean;
  onCopyLink: () => void;
  onCreatePortal: () => void;
  saving: boolean;
  onNavigateDocuments: () => void;
  onNavigateNotes: () => void;
  onNavigateComms: () => void;
  onOpenAgent: (kind: 'bidding' | 'marketing') => void;
}

const ClientWorkspaceSidebar: React.FC<ClientWorkspaceSidebarProps> = ({
  visibleDeals,
  archivedDeals,
  activeDealId,
  getDealStage,
  getDealDisplay,
  onSelectDeal,
  onRenameDeal,
  onTransferDeal,
  onToggleVisibility,
  onDeleteDeal,
  onAddProject,
  portal,
  copied,
  onCopyLink,
  onCreatePortal,
  saving,
  onNavigateDocuments,
  onNavigateNotes,
  onNavigateComms,
  onOpenAgent,
}) => {
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [menuDealId, setMenuDealId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuDealId) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuDealId(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuDealId]);

  const renderRow = (deal: EnhancedDeal, archivedRow: boolean) => {
    const { number, descriptor } = getDealDisplay(deal);
    const stage = getDealStage(deal);
    const isActive = deal.id === activeDealId;
    const menuOpen = menuDealId === deal.id;
    return (
      <div
        key={deal.id}
        className={`cw-project-row ${isActive ? 'active' : ''} ${archivedRow ? 'archived' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => deal.id && onSelectDeal(deal.id, { archivedRow })}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') deal.id && onSelectDeal(deal.id, { archivedRow });
        }}
      >
        <div className="cw-project-row-main">
          <span className="cw-project-row-title">
            {number && <span className="cw-project-row-number">{number}</span>}
            <span className="cw-project-row-descriptor" title={descriptor}>{descriptor}</span>
          </span>
          <span className="cw-project-row-meta">
            {stage && (
              <span className={`cw-stage-chip cw-stage-${stage}`}>
                {PORTAL_STAGE_SHORT_LABELS[stage]}
              </span>
            )}
            {deal.outcome === 'won' && <span className="cw-outcome-chip won">Won</span>}
            {deal.outcome === 'lost' && <span className="cw-outcome-chip lost">Lost</span>}
            {archivedRow && <EyeOff size={11} className="cw-project-row-hidden-icon" aria-label="Hidden from portal" />}
          </span>
        </div>
        <div className="cw-project-row-actions">
          <button
            type="button"
            className="cw-project-row-kebab"
            aria-label="Project actions"
            aria-expanded={menuOpen}
            onClick={e => {
              e.stopPropagation();
              setMenuDealId(menuOpen ? null : deal.id || null);
            }}
          >
            <MoreHorizontal size={14} />
          </button>
          {menuOpen && (
            <div className="cw-project-row-menu" ref={menuRef} onClick={e => e.stopPropagation()}>
              <button type="button" onClick={() => { setMenuDealId(null); onRenameDeal(deal); }}>
                <Pencil size={13} /> Rename
              </button>
              {onTransferDeal && (
                <button type="button" onClick={() => { setMenuDealId(null); onTransferDeal(deal); }}>
                  <ArrowRightLeft size={13} /> Transfer
                </button>
              )}
              <button type="button" onClick={() => { setMenuDealId(null); deal.id && onToggleVisibility(deal.id); }}>
                {archivedRow ? <Eye size={13} /> : <EyeOff size={13} />}
                {archivedRow ? 'Show on portal' : 'Hide from portal'}
              </button>
              <button
                type="button"
                className="danger"
                onClick={() => {
                  setMenuDealId(null);
                  deal.id && onDeleteDeal(deal.id, deal.projectName || deal.title || 'Project');
                }}
              >
                <Trash2 size={13} /> Delete
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <aside className="ops-sidebar cw-sidebar" data-tour="cw-sidebar">
      <div className="ops-sidebar-header">
        <span className="ops-sidebar-label"><FolderKanban size={12} /> Projects</span>
      </div>

      <div className="cw-sidebar-projects" data-tour="cw-sidebar-projects">
        {visibleDeals.length === 0 && archivedDeals.length === 0 && (
          <p className="cw-sidebar-empty">No projects yet.</p>
        )}
        {visibleDeals.map(deal => renderRow(deal, false))}

        {archivedDeals.length > 0 && (
          <>
            <button
              type="button"
              className={`cw-archived-toggle ${archivedOpen ? 'open' : ''}`}
              onClick={() => setArchivedOpen(o => !o)}
              aria-expanded={archivedOpen}
            >
              {archivedOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              <span>Hidden from portal</span>
              <span className="cw-archived-count">{archivedDeals.length}</span>
            </button>
            {archivedOpen && archivedDeals.map(deal => renderRow(deal, true))}
          </>
        )}

        <button type="button" className="cw-add-project-btn" onClick={onAddProject}>
          <Plus size={13} /> Add Project
        </button>
      </div>

      <div className="cw-sidebar-section" data-tour="cw-sidebar-portal">
        <span className="ops-sidebar-label">Client Portal</span>
        {portal ? (
          <div className="cw-portal-block">
            <a
              className="cw-portal-link"
              href={`/portal/${portal.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              title={`${window.location.origin}/portal/${portal.slug}`}
            >
              <Link2 size={12} />
              <span>/portal/{portal.slug}</span>
            </a>
            <div className="cw-portal-meta-row">
              <span className="cw-portal-pin">PIN {portal.accessPin}</span>
              <button type="button" className="cw-portal-btn" onClick={onCopyLink} title="Copy portal link + PIN">
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
              <a className="cw-portal-btn" href={`/portal/${portal.slug}`} target="_blank" rel="noopener noreferrer">
                <Eye size={12} /> Preview
              </a>
            </div>
          </div>
        ) : (
          <div className="cw-portal-block">
            <p className="cw-sidebar-empty"><Lock size={12} /> No portal yet.</p>
            <button type="button" className="cw-portal-create-btn" onClick={onCreatePortal} disabled={saving}>
              <Plus size={12} /> {saving ? 'Creating…' : 'Create Portal'}
            </button>
          </div>
        )}
      </div>

      <div className="cw-sidebar-section cw-sidebar-nav" data-tour="cw-sidebar-nav">
        <span className="ops-sidebar-label">Workspace</span>
        <button type="button" className="ops-sidebar-item" onClick={onNavigateDocuments}>
          <span className="ops-sidebar-item-icon"><FileText size={15} /></span>
          <span className="ops-sidebar-item-label">Documents</span>
        </button>
        <button type="button" className="ops-sidebar-item" onClick={onNavigateNotes}>
          <span className="ops-sidebar-item-icon"><MessageSquare size={15} /></span>
          <span className="ops-sidebar-item-label">Team Notes</span>
        </button>
        <button type="button" className="ops-sidebar-item" onClick={onNavigateComms}>
          <span className="ops-sidebar-item-icon"><Phone size={15} /></span>
          <span className="ops-sidebar-item-label">Comms</span>
        </button>
        <button type="button" className="ops-sidebar-item" onClick={() => onOpenAgent('bidding')}>
          <span className="ops-sidebar-item-icon"><Calculator size={15} /></span>
          <span className="ops-sidebar-item-label">Bidding Agent</span>
        </button>
        <button type="button" className="ops-sidebar-item" onClick={() => onOpenAgent('marketing')}>
          <span className="ops-sidebar-item-icon"><Sparkles size={15} /></span>
          <span className="ops-sidebar-item-label">Marketing Agent</span>
        </button>
      </div>
    </aside>
  );
};

export default ClientWorkspaceSidebar;
